// exportReportPpt.js
import { toPng } from "html-to-image";
import PptxGenJS from "pptxgenjs";

const DEFAULT_EXPORT_OPTIONS = {
  includeTitleSlide: true,
  includeAvoidedTrend: true,
  includePickups: true,
  includeSankeyQty: true,
  includeSankeyAvoided: true,
  includeMaterialsTable: true,
  tableRowsPerSlide: 14,
};

export async function exportReportPpt({
  reportFromStr,
  reportToStr,
  darkMode,
  emissionsPeriod,

  receiverMaterialQuantities,
  emissionsTrendInReportRange,

  trendChartEl,
  pickupsChartEl,
  sankeyQtyEl,
  sankeyAvoidedEl,

  unitLabel = "",

  pickupsTitle = "Pickups quantity over time",
  pickupsSubtitle = "Total delivered quantity per period",
  pickupsMetricLabel = "Quantity",

  sankeyQtyTitle = "Sankey breakdown: Quantity",
  sankeyQtySubtitle = "Clients → Materials → Quantity",
  sankeyAvoidedTitle = "Sankey breakdown: Avoided emissions",
  sankeyAvoidedSubtitle = "Clients → Materials → Avoided impact",

  exportOptions = {},
} = {}) {
  const opts = { ...DEFAULT_EXPORT_OPTIONS, ...(exportOptions || {}) };

  const fromText = reportFromStr ? reportFromStr : "All time";
  const toText = reportToStr ? reportToStr : "All time";

  // ---------- Capture helper ----------
  const capture = async (el) => {
    if (!el) return null;
    try {
      return await toPng(el, {
        cacheBust: true,
        pixelRatio: 3,
        backgroundColor: darkMode ? "#0b0f14" : "#ffffff",
      });
    } catch (e) {
      console.error("Capture failed:", e);
      return null;
    }
  };

  // Only capture what we actually intend to include
  const [
    chartDataUrl,
    pickupsChartDataUrl,
    sankeyQtyDataUrl,
    sankeyAvoidedDataUrl,
  ] = await Promise.all([
    opts.includeAvoidedTrend ? capture(trendChartEl) : Promise.resolve(null),
    opts.includePickups ? capture(pickupsChartEl) : Promise.resolve(null),
    opts.includeSankeyQty ? capture(sankeyQtyEl) : Promise.resolve(null),
    opts.includeSankeyAvoided ? capture(sankeyAvoidedEl) : Promise.resolve(null),
  ]);

  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE";

  // ---------- THEME ----------
  const THEME = darkMode
    ? {
        bg: "070B14",
        panel: "0B1220",
        text: "FFFFFF",
        muted: "B9C6D6",
        accent: "67A3FF",
        accent2: "2F6FED",
        border: "1D2A44",
        soft: "0F1A2E",
      }
    : {
        bg: "FFFFFF",
        panel: "F6F9FF",
        text: "0A1222",
        muted: "40506A",
        accent: "2F6FED",
        accent2: "67A3FF",
        border: "D7E2F2",
        soft: "EEF4FF",
      };

  const SLIDE_W = 13.33;

  const fmtNum = (n, digits = 2) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return "—";
    return x.toFixed(digits);
  };

  const totalQty = (receiverMaterialQuantities || []).reduce(
    (s, r) => s + (Number(r?.qty) || 0),
    0
  );

  const totalAvoided = (emissionsTrendInReportRange || []).reduce(
    (s, r) => s + (Number(r?.avoided) || 0),
    0
  );

  const avoidedLabel = unitLabel
    ? `Avoided emissions (${unitLabel})`
    : "Avoided emissions";

  // ---------- HELPERS ----------
  const addBackground = (slide) => {
    slide.background = { color: THEME.bg };
    slide.addShape(pptx.ShapeType.rect, {
      x: 0,
      y: 0,
      w: SLIDE_W,
      h: 0.12,
      fill: { color: THEME.accent2 },
      line: { color: THEME.accent2 },
    });
    slide.addShape(pptx.ShapeType.rect, {
      x: 0,
      y: 0.12,
      w: SLIDE_W,
      h: 0.28,
      fill: { color: THEME.soft, transparency: 15 },
      line: { color: THEME.soft, transparency: 100 },
    });
  };

  const addHeader = (slide, title, subtitle) => {
    slide.addText(title, {
      x: 0.6,
      y: 0.45,
      w: 12.2,
      h: 0.45,
      fontFace: "Aptos Display",
      fontSize: 26,
      bold: true,
      color: THEME.text,
    });

    if (subtitle) {
      slide.addText(subtitle, {
        x: 0.6,
        y: 0.92,
        w: 12.2,
        h: 0.3,
        fontFace: "Aptos",
        fontSize: 12,
        color: THEME.muted,
      });
    }
  };

  const addFooter = (slide, pageNum, pageTotal) => {
    const left = `Range: ${fromText} → ${toText}`;
    const right = `${pageNum}/${pageTotal}`;

    slide.addText(left, {
      x: 0.6,
      y: 7.12,
      w: 9.8,
      h: 0.25,
      fontFace: "Aptos",
      fontSize: 10,
      color: THEME.muted,
    });

    slide.addText(right, {
      x: 11.9,
      y: 7.12,
      w: 0.8,
      h: 0.25,
      align: "right",
      fontFace: "Aptos",
      fontSize: 10,
      color: THEME.muted,
    });
  };

  const addCard = (slide, { x, y, w, h, title, value, sub }) => {
    slide.addShape(pptx.ShapeType.roundRect, {
      x,
      y,
      w,
      h,
      fill: { color: THEME.panel },
      line: { color: THEME.border, width: 1 },
      radius: 10,
    });

    slide.addShape(pptx.ShapeType.roundRect, {
      x,
      y,
      w: 0.12,
      h,
      fill: { color: THEME.accent2 },
      line: { color: THEME.accent2 },
      radius: 10,
    });

    slide.addText(title, {
      x: x + 0.25,
      y: y + 0.18,
      w: w - 0.35,
      h: 0.25,
      fontFace: "Aptos",
      fontSize: 11,
      color: THEME.muted,
    });

    slide.addText(value, {
      x: x + 0.25,
      y: y + 0.48,
      w: w - 0.35,
      h: 0.5,
      fontFace: "Aptos Display",
      fontSize: 20,
      bold: true,
      color: THEME.text,
    });

    if (sub) {
      slide.addText(sub, {
        x: x + 0.25,
        y: y + 0.98,
        w: w - 0.35,
        h: 0.25,
        fontFace: "Aptos",
        fontSize: 10,
        color: THEME.muted,
      });
    }
  };

  const addFramedImageSlide = ({
    title,
    subtitle,
    metricLine,
    dataUrl,
    footerLine,
  }) => {
    const slide = pptx.addSlide();
    addBackground(slide);
    addHeader(slide, title, subtitle);

    const frameX = 0.5;
    const frameY = 1.25;
    const frameW = 12.3;
    const frameH = 5.25;
    const pad = 0.18;

    slide.addShape(pptx.ShapeType.roundRect, {
      x: frameX,
      y: frameY,
      w: frameW,
      h: frameH,
      fill: { color: THEME.panel },
      line: { color: THEME.border, width: 1 },
      radius: 16,
    });

    if (metricLine) {
      slide.addShape(pptx.ShapeType.roundRect, {
        x: frameX + 0.3,
        y: frameY + 0.18,
        w: 9.0,
        h: 0.32,
        fill: { color: THEME.soft },
        line: { color: THEME.border, width: 1 },
        radius: 10,
      });

      slide.addText(metricLine, {
        x: frameX + 0.42,
        y: frameY + 0.21,
        w: 8.8,
        h: 0.25,
        fontFace: "Aptos",
        fontSize: 10,
        color: THEME.muted,
      });
    }

    if (dataUrl) {
      slide.addImage({
        data: dataUrl,
        x: frameX + pad,
        y: frameY + 0.7,
        w: frameW - pad * 2,
        h: frameH - 1.2,
      });
    } else {
      slide.addText("Chart could not be captured.", {
        x: frameX + pad,
        y: frameY + frameH / 2 - 0.25,
        w: frameW - pad * 2,
        h: 0.5,
        fontFace: "Aptos",
        fontSize: 14,
        color: THEME.muted,
        align: "center",
      });
    }

    if (footerLine) {
      slide.addShape(pptx.ShapeType.roundRect, {
        x: 0.6,
        y: 6.7,
        w: 12.2,
        h: 0.3,
        fill: { color: THEME.soft },
        line: { color: THEME.border, width: 1 },
        radius: 10,
      });

      slide.addText(footerLine, {
        x: 0.85,
        y: 6.76,
        w: 11.7,
        h: 0.2,
        fontFace: "Aptos",
        fontSize: 10,
        color: THEME.muted,
      });
    }

    return slide;
  };

  // ---------- TABLE PAGINATION ----------
  const tableRows = (receiverMaterialQuantities || []).map((r) => ({
    material: r.material,
    qty: r.qty,
  }));

  const maxBodyRowsPerSlide = Math.max(4, Number(opts.tableRowsPerSlide) || 14);

  const tableChunks = tableRows.length
    ? Array.from(
        { length: Math.ceil(tableRows.length / maxBodyRowsPerSlide) },
        (_, i) =>
          tableRows.slice(i * maxBodyRowsPerSlide, (i + 1) * maxBodyRowsPerSlide)
      )
    : [[]];

  // Slides we will actually include
  const includeTitle = opts.includeTitleSlide;
  const includeAvoided = opts.includeAvoidedTrend;
  const includePickups = opts.includePickups;
  const includeSQty = opts.includeSankeyQty;
  const includeSAvoid = opts.includeSankeyAvoided;
  const includeTable = opts.includeMaterialsTable;

  const totalSlides =
    (includeTitle ? 1 : 0) +
    (includeAvoided ? 1 : 0) +
    (includePickups ? 1 : 0) +
    (includeSQty ? 1 : 0) +
    (includeSAvoid ? 1 : 0) +
    (includeTable ? tableChunks.length : 0);

  let page = 1;

  // ---------- TITLE SLIDE ----------
  if (includeTitle) {
    const slide = pptx.addSlide();
    addBackground(slide);

    addHeader(
      slide,
      "GHG Emissions Report",
      "Environmental impact summary (receiver allocations & avoided emissions)"
    );

    slide.addShape(pptx.ShapeType.roundRect, {
      x: 0.6,
      y: 1.4,
      w: 12.2,
      h: 2.1,
      fill: { color: THEME.panel },
      line: { color: THEME.border, width: 1 },
      radius: 16,
    });

    slide.addText(`Date range`, {
      x: 1.0,
      y: 1.65,
      w: 5.5,
      h: 0.3,
      fontFace: "Aptos",
      fontSize: 12,
      color: THEME.muted,
    });

    slide.addText(`${fromText}  →  ${toText}`, {
      x: 1.0,
      y: 1.95,
      w: 11.0,
      h: 0.5,
      fontFace: "Aptos Display",
      fontSize: 22,
      bold: true,
      color: THEME.text,
    });

    slide.addText(`Trend period: ${String(emissionsPeriod).toUpperCase()}`, {
      x: 1.0,
      y: 2.55,
      w: 11.0,
      h: 0.3,
      fontFace: "Aptos",
      fontSize: 12,
      color: THEME.muted,
    });

    const kY = 3.85;
    const kH = 1.55;
    const gap = 0.15;
    const kX0 = 0.6;
    const kW = (12.2 - gap * 2) / 3;

    addCard(slide, {
      x: kX0 + (kW + gap) * 0,
      y: kY,
      w: kW,
      h: kH,
      title: "Materials in range",
      value: String((receiverMaterialQuantities || []).length),
      sub: "Unique material types delivered",
    });

    addCard(slide, {
      x: kX0 + (kW + gap) * 1,
      y: kY,
      w: kW,
      h: kH,
      title: "Total quantity (range)",
      value: fmtNum(totalQty, 2),
      sub: "Sum of delivered quantities",
    });

    addCard(slide, {
      x: kX0 + (kW + gap) * 2,
      y: kY,
      w: kW,
      h: kH,
      title: "Total avoided emissions",
      value: fmtNum(totalAvoided, 2),
      sub: unitLabel || "—",
    });

    addFooter(slide, page++, totalSlides);
  }

  // ---------- Avoided chart ----------
  if (includeAvoided) {
    const slide = addFramedImageSlide({
      title: `${avoidedLabel} over time`,
      subtitle: "Each point sums avoided emissions from delivered requests in that period",
      metricLine: `Period: ${emissionsPeriod}  •  Metric: ${avoidedLabel}`,
      dataUrl: chartDataUrl,
      footerLine: `Avoided = Qty × (Emission Factor − Recycling Emissions)  •  Total avoided: ${fmtNum(
        totalAvoided,
        2
      )}${unitLabel ? ` ${unitLabel}` : ""}`,
    });

    addFooter(slide, page++, totalSlides);
  }

  // ---------- Pickups ----------
  if (includePickups) {
    const slide = addFramedImageSlide({
      title: pickupsTitle,
      subtitle: pickupsSubtitle,
      metricLine: `Period: ${emissionsPeriod}  •  Metric: ${pickupsMetricLabel}`,
      dataUrl: pickupsChartDataUrl,
    });

    addFooter(slide, page++, totalSlides);
  }

  // ---------- Sankey Qty ----------
  if (includeSQty) {
    const slide = addFramedImageSlide({
      title: sankeyQtyTitle,
      subtitle: sankeyQtySubtitle,
      metricLine: `Period: ${emissionsPeriod}  •  Flow: Quantity`,
      dataUrl: sankeyQtyDataUrl,
    });

    addFooter(slide, page++, totalSlides);
  }

  // ---------- Sankey Avoided ----------
  if (includeSAvoid) {
    const slide = addFramedImageSlide({
      title: sankeyAvoidedTitle,
      subtitle: sankeyAvoidedSubtitle,
      metricLine: `Period: ${emissionsPeriod}  •  Flow: Avoided (${unitLabel || "unit"})`,
      dataUrl: sankeyAvoidedDataUrl,
    });

    addFooter(slide, page++, totalSlides);
  }

  // ---------- Table ----------
  if (includeTable) {
    const makeTableRow = (material, qty, isAlt) => {
      const fill = isAlt ? THEME.soft : THEME.panel;
      return [
        { text: String(material), options: { color: THEME.text, fill } },
        {
          text: String(Number(qty).toFixed(2)),
          options: { color: THEME.text, fill },
        },
      ];
    };

    const headerRow = [
      { text: "Material", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
      { text: "Quantity", options: { bold: true, color: THEME.text, fill: THEME.accent2 } },
    ];

    tableChunks.forEach((chunk, idx) => {
      const slide = pptx.addSlide();
      addBackground(slide);

      const suffix =
        tableChunks.length > 1 ? ` (Page ${idx + 1}/${tableChunks.length})` : "";

      addHeader(slide, `Material types and quantities${suffix}`, "Delivered quantities by material");

      slide.addShape(pptx.ShapeType.roundRect, {
        x: 0.6,
        y: 1.35,
        w: 12.2,
        h: 5.6,
        fill: { color: THEME.panel },
        line: { color: THEME.border, width: 1 },
        radius: 16,
      });

      const bodyRows =
        chunk.length > 0
          ? chunk.map((r, i) => makeTableRow(r.material, r.qty, i % 2 === 1))
          : [makeTableRow("—", "—", false)];

      slide.addTable([headerRow, ...bodyRows], {
        x: 0.9,
        y: 1.75,
        w: 11.6,
        h: 4.85,
        fontFace: "Aptos",
        fontSize: 10,
        color: THEME.text,
        border: { type: "solid", color: THEME.border, pt: 1 },
        valign: "mid",
        colW: [8.5, 3.1],
      });

      addFooter(slide, page++, totalSlides);
    });
  }

  const safeFrom = reportFromStr ? reportFromStr : "all";
  const safeTo = reportToStr ? reportToStr : "all";
  const fileName = `GHG_Report_${safeFrom}_to_${safeTo}.pptx`;

  await pptx.writeFile({ fileName });
}
